import React, {useEffect, useRef, useState} from "react";
import FlexContainer from "connectui/component/layout/FlexContainer";
import {useTheme} from "connectui/theme/ThemeProvider";
import Text from "connectui/component/display/Text";
import CircularSpinner from "connectui/component/display/CircularSpinner";
import {useDispatch, useSelector} from "react-redux";
import {ReactComponent as WhatsAppIcon} from "connectui/icons/WhatsApp-card.svg";
import {getTwilioServiceAccount} from "redux/actions/servicesActions";
import {messagingSettingsStageEnum} from "../BaseMessagingSettingsPanel";
import ButtonText from "connectui/component/input/button/ButtonText";
import TextButtonBase from "connectui/component/input/button/base/TextButtonBase";
import SvgIcon from "connectui/component/display/SvgIcon";
import ButtonIconText from "connectui/component/input/button/ButtonIconText";
import {
  closeGeneralError,
  closeGeneralSuccessAlert,
  openGeneralError,
  openGeneralSuccessAlert,
} from "redux/actions/settingsActions";
import {postAccount} from "redux/api/accountsAPI";

const FACEBOOK_CONFIG = {
  APP_ID: "564314409751455",
  CONFIG_ID: "2433746020129195",
  SOLUTION_ID: "616090990953033",
};

const loadFacebookSDK = () => {
  if (window.FB) {
    return Promise.resolve(window.FB);
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;

    script.onload = () => {
      window.FB.init({
        appId: FACEBOOK_CONFIG.APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: "v21.0",
      });
      resolve(window.FB);
    };
    script.onerror = reject;

    document.body.appendChild(script);
  });
};

const WhatsAppManageEmbeddedFlowPanel = ({setStage, data}) => {
  const {theme} = useTheme();
  const dispatch = useDispatch();

  const [fbLoginLoading, setFbLoginLoading] = useState(false);
  const [fbLoginError, setFbLoginError] = useState("");
  const fbLoginErrorRef = useRef(fbLoginError);

  useEffect(() => {
    fbLoginErrorRef.current = fbLoginError;
  }, [fbLoginError]);

  const [postAccountLoading, setPostAccountLoading] = useState(false);
  const [postAccountError, setPostAccountError] = useState("");

  let {selectedPhone, type} = data;

  function parsePhoneNumber(phone_number) {
    return !!phone_number &&
      typeof phone_number === "string" &&
      !phone_number.startsWith("+")
      ? `+${phone_number}`
      : phone_number;
  }
  selectedPhone = parsePhoneNumber(selectedPhone);

  const serviceAccountsLoading = useSelector(
    (state) => state.defaultReducer.loading.service_accounts,
  );
  const twilioServiceAccount = useSelector(
    (state) => state.defaultReducer.service_accounts?.accounts?.twilio?.[0],
  );
  const serviceAccountExists =
    !!twilioServiceAccount &&
    Object.keys(twilioServiceAccount).length > 0 &&
    twilioServiceAccount.properties?.["whatsapp_sender_id"];

  const handleFbLoginError = (message) => {
    setFbLoginLoading(false);
    setFbLoginError(message || "");
    dispatch(
      openGeneralError({
        message,
        onClose: () => dispatch(closeGeneralError()),
      }),
    );
  };

  const embeddedSignupInfoListener = (event) => {
    if (!event.origin.endsWith("facebook.com")) return;

    try {
      const eventData = JSON.parse(event.data);

      // We only care about messages related to WA_EMBEDDED_SIGNUP
      if (eventData.type === "WA_EMBEDDED_SIGNUP") {
        switch (eventData.event) {
          case "FINISH":
          case "FINISH_ONLY_WABA": {
            // handle success scenario here.

            const {phone_number_id, waba_id} = eventData.data;

            setPostAccountError("");
            setPostAccountLoading(true);
            postAccount({
              body: {
                data: {
                  action_id: "create_messaging_account",
                  provider: "whatsapp",
                  integration_data: {
                    phone_number_id: phone_number_id,
                    waba_id: waba_id,
                    phone_number: selectedPhone,
                  },
                },
              },
            })
              .then((response) => {
                setPostAccountError("");
                dispatch(
                  openGeneralSuccessAlert({
                    message: "Successfully created account",
                    onClose: () => dispatch(closeGeneralSuccessAlert()),
                  }),
                );
                setStage({
                  id: messagingSettingsStageEnum.settings,
                });
              })
              .catch((error) => {
                let ensoError = error?.response?.data?.error?.[0];
                setPostAccountError(
                  ensoError?.message ||
                    "Creating service account failed. Please try again later.",
                );
              })
              .finally(() => {
                setPostAccountLoading(false);
              });
            break;
          }
          case "CANCEL":
            handleFbLoginError(
              "Facebook login was cancelled. Please try again.",
            );
            break;
          case "ERROR":
            handleFbLoginError(
              `Facebook login error: ${eventData.data.error_message}`,
            );
            break;
          default:
            break;
        }
      }
    } catch {
      // Ignore non JSON responses
      // console.log("Non JSON Response", event.data);
    }
  };

  const handleConnectWhatsAppClick = () => {
    if (!window.FB) {
      setFbLoginLoading(false);
      setFbLoginError("Facebook SDK is not available.");
      console.error("Facebook SDK is not available.");
      return;
    }

    setFbLoginLoading(true);
    setFbLoginError("");

    window.FB.login(
      ({authResponse}) => {
        setFbLoginLoading(false);
        if (!authResponse && !fbLoginErrorRef.current) {
          handleFbLoginError(
            "Facebook login did not complete as expected. Please try again.",
          );
        }
      },
      {
        config_id: FACEBOOK_CONFIG.CONFIG_ID,
        auth_type: "rerequest",
        response_type: "code",
        override_default_response_type: true,
        extras: {
          sessionInfoVersion: 3,
          featureType: type == "managed" && "only_waba_sharing",
          setup: {
            solutionID: FACEBOOK_CONFIG.SOLUTION_ID,
          },
        },
      },
    );
  };

  useEffect(() => {
    loadFacebookSDK().catch(() =>
      dispatch(
        openGeneralError({
          message: "There was a problem loading the Facebook SDK.",
          subtitle: "Something went wrong, please try again later.",
          onClose: () => dispatch(closeGeneralError()),
        }),
      ),
    );
  }, [dispatch]);

  useEffect(() => {
    dispatch(getTwilioServiceAccount({}));
  }, [dispatch]);

  useEffect(() => {
    window.addEventListener("message", embeddedSignupInfoListener);
    return () => {
      window.removeEventListener("message", embeddedSignupInfoListener);
    };
  }, [embeddedSignupInfoListener]);

  return (
    <FlexContainer
      flexDirection="column"
      alignItems="flex-start"
      justifyContent="center"
      gap={theme.spacing(5)}
      sx={{paddingBottom: theme.spacing(3), width: "100%", maxWidth: "660px"}}
    >
      <Text variant="displayXS" weight="semibold">
        Connect WhatsApp Account
      </Text>
      {serviceAccountsLoading ? (
        <>
          <CircularSpinner />
          <Text variant="textSM" sx={{color: theme.colors.textSecondary}}>
            Loading...
          </Text>
        </>
      ) : serviceAccountExists ? (
        <>
          <Text variant="textMD" weight="medium">
            A WhatsApp Service Account already exists for your account.
          </Text>
          <ButtonText
            label="Return to Settings"
            typographyVariant="textSM"
            weight="semibold"
            sx={{
              width: "fit-content",
              padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
            }}
            color="white"
            onClick={() => setStage({id: messagingSettingsStageEnum.settings})}
          />
        </>
      ) : (
        <>
          <Text variant="textSM" sx={{color: theme.colors.textSecondary}}>
            Selected phone number: {selectedPhone}
          </Text>
          <Text variant="textSM" sx={{color: theme.colors.textSecondary}}>
            Continue the account connection on the Facebook Sign-in tab.
          </Text>
          <ButtonIconText
            buttonBase={TextButtonBase}
            typographyVariant="textSM"
            weight="semibold"
            color="primary"
            label="Connect WhatsApp Account"
            sx={{width: "fit-content"}}
            onClick={handleConnectWhatsAppClick}
            icon={
              <SvgIcon
                component={WhatsAppIcon}
                sx={{
                  color: theme.colors.primary,
                  paddingRight: theme.spacing(1),
                }}
              />
            }
          />
          {(fbLoginLoading || postAccountLoading) && (
            <>
              <CircularSpinner />
              <Text variant="textSM" sx={{color: theme.colors.textSecondary}}>
                Do not close this page until the Meta WhatsApp Embedded process
                is completed.
              </Text>
              <Text variant="textSM" sx={{color: theme.colors.textSecondary}}>
                If you are stuck, please refresh the page and restart the
                connection.
              </Text>
            </>
          )}
          {!!fbLoginError && (
            <Text weight="medium" color="error500">
              {fbLoginError}
            </Text>
          )}
          {!!postAccountError && (
            <Text
              weight="medium"
              color="error500"
              sx={{whiteSpace: "pre-line"}}
            >
              {postAccountError}
            </Text>
          )}
        </>
      )}
    </FlexContainer>
  );
};

export default WhatsAppManageEmbeddedFlowPanel;
